import { Dialog, ModalV2, ButtonV2 } from '@cimplico/components';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import { toastNotification } from '../../../../components/toast/Toast';
import { useDeleteSubscriptionScheduleV2 } from '../../../../queries/billing/use-delete-subscription-schedulev2';
import { SubscriptionV2 } from '../../../../types/firm';
import { formatDate } from '../../../../utilities/formatting';

type ScheduledChangesProps = {
    subscription: SubscriptionV2;
    onClose: () => void;
};
function ScheduledChangesAlert({ subscription, onClose }: ScheduledChangesProps): JSX.Element {
    const [confirmCancel, setConfirmCancel] = useState(false);
    const deleteScheduleQuery = useDeleteSubscriptionScheduleV2();

    function deleteSchedule(): void {
        deleteScheduleQuery.mutate(undefined, {
            onSuccess: () => {
                toastNotification('success', 'Scheduled changes cancelled');
                onClose();
            },
            onError: () => {
                toastNotification('error', 'Unable to cancel scheduled changes. Something went wrong');
            },
        });
    }

    if (!subscription?.scheduledChange) {
        return <></>;
    }

    return (
        <>
            {confirmCancel ? (
                <Dialog
                    title="Cancel scheduled changes"
                    description={`This will remove any changes scheduled for ${formatDate(
                        subscription.scheduledChange.updateScheduledAt
                    )}.`}
                    type="error"
                    isOpen
                    primaryAction="Confirm"
                    secondaryAction="Close"
                    onClose={(): void => setConfirmCancel(false)}
                    onSecondaryActionClick={(): void => setConfirmCancel(false)}
                    onPrimaryActionClick={deleteSchedule}
                    isLoading={deleteScheduleQuery.isLoading}
                />
            ) : (
                <ModalV2 onClose={onClose} isOpen>
                    <ModalV2.Body>
                        <div className="text-left mb-4">
                            <p className="text-sm text-contentPrimary font-bold">Scheduled changes</p>
                            <p className="text-sm text-contentSecondary">
                                The following changes to your subscription will apply on:{' '}
                                <b>{formatDate(subscription.scheduledChange.updateScheduledAt)}</b>
                            </p>
                        </div>

                        <div className="text-left">
                            {subscription.scheduledChange.tier && (
                                <>
                                    <p className="font-medium uppercase text-contentPrimary">Tier</p>
                                    <div className="flex items-center gap-4 mt-2">
                                        <p className="text-sm text-contentPrimary capitalize">{subscription.tier.toLowerCase()}</p>
                                        <ArrowRightIcon className="text-primary h-4 w-4 stroke-2" />
                                        <p className="text-sm text-contentPrimary capitalize">
                                            {subscription.scheduledChange.tier.toLowerCase()}
                                        </p>
                                    </div>
                                </>
                            )}
                            {subscription.scheduledChange.interval && (
                                <>
                                    <p className="font-medium uppercase text-contentPrimary mt-4">Billing Interval</p>
                                    <div className="flex items-center gap-4 mt-2">
                                        <p className="text-sm text-contentPrimary capitalize">{subscription.interval.toLowerCase()}</p>
                                        <ArrowRightIcon className="text-primary h-4 w-4 stroke-2" />
                                        <p className="text-sm text-contentPrimary capitalize">
                                            {subscription.scheduledChange.interval.toLowerCase()}
                                        </p>
                                    </div>
                                </>
                            )}

                            {typeof subscription.scheduledChange.additionalEntitySlots === 'number' && (
                                <>
                                    <p className="font-medium uppercase text-contentPrimary mt-4">Additional Entity Slots</p>
                                    <div className="flex items-center gap-4 mt-2">
                                        <p className="text-sm text-contentPrimary capitalize">{subscription.additionalEntitySlots}</p>
                                        <ArrowRightIcon className="text-primary h-4 w-4 stroke-2" />
                                        <p className="text-sm text-contentPrimary capitalize">
                                            {subscription.scheduledChange.additionalEntitySlots}
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>
                    </ModalV2.Body>

                    <ModalV2.Footer>
                        <div className="flex justify-between gap-2">
                            <ButtonV2 size="sm" type="danger" label="Cancel changes" onClick={(): void => setConfirmCancel(true)} />
                            <ButtonV2 size="sm" type="secondary" label="Close" onClick={onClose} />
                        </div>
                    </ModalV2.Footer>
                </ModalV2>
            )}
        </>
    );
}

export default ScheduledChangesAlert;
